import gsap from 'gsap';

interface ButtonModularState {
  index: number;
  globalStyle: string;
  labelStyle: string;
  status: string;
  type: string;
  ariaLabel: string;
}

export default class ButtonModular extends HTMLElement {
  private button: HTMLButtonElement | null = null;
  private labels: HTMLElement[] = [];
  private previousStateIndex: number | null = null;
  private currentStateIndex: number = 0;
  private states: ButtonModularState[] = [];

  private goToStateTl: gsap.core.Timeline | null = null;

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.button = this.querySelector('[data-button-modular-ref="button"]');
    this.labels = Array.from(this.querySelectorAll('[data-button-modular-ref="labels"]')) as HTMLElement[];

    const initialState = Number(this.dataset.initialState);
    this.currentStateIndex = Number.isFinite(initialState) && initialState >= 0 ? initialState : 0;

    for (let i = 0; i < this.labels.length; i++) {
      this.states.push({
        index: i,
        globalStyle: this.labels[i].getAttribute('data-background-style') || '',
        labelStyle: this.labels[i].getAttribute('data-label-style') || '',
        status: this.labels[i].getAttribute('data-status') || '',
        type: this.labels[i].getAttribute('data-type') || '',
        ariaLabel: this.labels[i].getAttribute('data-aria-label') || '',
      });
    }

    if (this.currentStateIndex >= this.states.length) {
      this.currentStateIndex = 0;
    }

    this.addEventListeners();
  }

  public gotToStateIndex(index: number): void {
    if (index < 0 || index >= this.states.length || index === this.currentStateIndex) {
      return;
    }

    if (this.goToStateTl) {
      this.goToStateTl.kill();
    }

    this.goToStateTl = gsap.timeline();

    this.previousStateIndex = this.currentStateIndex;
    this.currentStateIndex = index;

    if (this.button) {
      const previousGlobalStyleClasses = this.states[this.previousStateIndex].globalStyle.split(' ');
      const currentGlobalStyleClasses = this.states[this.currentStateIndex].globalStyle.split(' ');

      previousGlobalStyleClasses.forEach((className) => {
        if (this.button && className) {
          this.button.classList.remove(className);
        }
      });
      currentGlobalStyleClasses.forEach((className) => {
        if (this.button && className) {
          this.button.classList.add(className);
        }
      });

      const previousStatus = this.states[this.previousStateIndex].status;
      const currentStatus = this.states[this.currentStateIndex].status;

      if (previousStatus) {
        this.button.removeAttribute(previousStatus);
      }
      if (currentStatus) {
        this.button.setAttribute(currentStatus, currentStatus);
      }

      const currentType = this.states[this.currentStateIndex].type;
      if (currentType) {
        this.button.type = currentType as HTMLButtonElement['type'];
      }

      const currentAriaLabel = this.states[this.currentStateIndex].ariaLabel;
      if (currentAriaLabel) {
        this.button.setAttribute('aria-label', currentAriaLabel);
      } else {
        this.button.removeAttribute('aria-label');
      }

      const previousLabel = this.labels[this.previousStateIndex];
      const currentLabel = this.labels[this.currentStateIndex];

      const isSameLabel = previousLabel.textContent === currentLabel.textContent;

      if (previousLabel && currentLabel) {
        this.goToStateTl.to(
          previousLabel,
          {
            autoAlpha: 0,
            duration: isSameLabel ? 0 : 0.2,
            ease: 'power2.out',
          },
          0,
        );
        this.goToStateTl.to(
          currentLabel,
          {
            autoAlpha: 1,
            duration: isSameLabel ? 0 : 0.2,
            ease: 'power2.out',
          },
          isSameLabel ? 0 : 0.3,
        );
      }
    }
  }

  addEventListeners(): void {}

  removeEventListeners(): void {}

  disconnectedCallback(): void {
    this.removeEventListeners();
  }
}

customElements.define('button-modular', ButtonModular);
